/**
 * Team-member write helpers — thin wrappers around the existing
 * Cloud Function callables. Web side just routes the call; all
 * permission + capacity + role-hierarchy validation lives server-
 * side in `functions/src/invites.ts`.
 *
 * Callables exposed:
 *   - `inviteMember`    — phone-based invite. If the phone matches an
 *                         existing user, they're added immediately +
 *                         their custom claims refreshed. Otherwise a
 *                         pending row is written to
 *                         `organizations/{orgId}/pendingInvites/{phone}`
 *                         and claimed on their first sign-in.
 *   - `removeMember`    — remove by uid (joined member) OR by phone
 *                         (cancel pending invite). Also strips the
 *                         uid from every project's memberIds/clientUids
 *                         (server-side, in a batched write).
 *   - `setMemberRole`   — change a joined member's org role. Mirrors
 *                         the role transition in/out of `client` to
 *                         project `memberIds` ↔ `clientUids`.
 *
 * For project-scoped membership we always pass `projectIds:
 * [projectId]` so non-admin invites only see this project on
 * sign-in. Admin/manager/accountant get all projects by virtue of
 * their org role (server enforces).
 */
import { httpsCallable } from 'firebase/functions';
import {
  arrayRemove,
  doc,
  updateDoc,
} from 'firebase/firestore';

import { db, functions } from '@/lib/firebase';
import type { AssignableRole, RoleKey } from '@/lib/useProjectMembers';

// ───── Callable wrappers ─────

export type InviteMemberArgs = {
  orgId: string;
  phoneNumber: string;
  role: AssignableRole;
  /** Project ids this invite scopes the user to (drives project
   *  visibility for non-admin roles). Pass [projectId] when adding
   *  to a specific project. */
  projectIds: string[];
  /** Optional friendly name written to the invite + memberPublic. */
  displayName?: string;
};

export type InviteMemberResult = {
  joinedNow: boolean;
  uid?: string;
};

export async function inviteProjectMember(
  args: InviteMemberArgs,
): Promise<InviteMemberResult> {
  const fn = httpsCallable<InviteMemberArgs, InviteMemberResult>(
    functions,
    'inviteMember',
  );
  const { data } = await fn(args);
  return data;
}

export type RemoveMemberArgs =
  | { orgId: string; uid: string }
  | { orgId: string; phoneNumber: string };

export async function removeProjectMember(
  args: RemoveMemberArgs,
): Promise<void> {
  const fn = httpsCallable<RemoveMemberArgs, { ok: true }>(
    functions,
    'removeMember',
  );
  await fn(args);
}

export type SetMemberRoleArgs = {
  orgId: string;
  uid: string;
  role: AssignableRole;
};

export async function setMemberRoleCall(
  args: SetMemberRoleArgs,
): Promise<void> {
  const fn = httpsCallable<SetMemberRoleArgs, { ok: true }>(
    functions,
    'setMemberRole',
  );
  await fn(args);
}

// ───── Direct project writes ─────

/**
 * Remove a uid from ONE project's `memberIds` or `clientUids` array
 * without removing them from the org. Used by the project Team
 * slide-over's "Remove from project" action (keeps them in the org
 * for other projects, just demotes them off this one).
 *
 * Mirrors the mobile pattern in
 * `app/(app)/projects/[id]/members.tsx:205-212`.
 *
 * Firestore rules already permit any org admin to update these
 * arrays on a project doc — no callable needed.
 */
export async function removeUidFromProject(args: {
  projectId: string;
  uid: string;
  asClient: boolean;
}): Promise<void> {
  const ref = doc(db, 'projects', args.projectId);
  if (args.asClient) {
    await updateDoc(ref, { clientUids: arrayRemove(args.uid) });
  } else {
    await updateDoc(ref, { memberIds: arrayRemove(args.uid) });
  }
}

// ───── Phone normalisation (mirrors server) ─────

/** Lightweight client-side check — server normalises authoritatively
 *  but we surface a friendlier error before the network round-trip. */
export function normalizeIndianPhoneE164(raw: string): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D+/g, '');
    if (digits.length < 8 || digits.length > 15) return null;
    return `+${digits}`;
  }
  const digits = trimmed.replace(/\D+/g, '');
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 11 && digits.startsWith('0')) return `+91${digits.slice(1)}`;
  if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
  if (digits.length >= 8 && digits.length <= 15) return `+${digits}`;
  return null;
}

// Re-export type so consumers don't need a second import.
export type { AssignableRole, RoleKey };
